import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useFieldArray, Controller } from 'react-hook-form';
import { Input, Table, Form, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiContentCopy,
  mdiPlusCircleOutline,
  mdiTrashCanOutline,
} from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import { useHotelBooking } from '../context/HotelBookingContext';
import { hotelBookingHeaders } from './constants';
import { fetchTravelRequestEnquiryFilter } from '../../../store/apis/travelService';
import { fetchPickListDetailsAutoComplete } from '../../../store/apis/autocompleteService';
import BookingAsyncAutocomplete from '../FlightOption/BookingAsyncAutocomplete';
import TravelAsyncAutocompleteSelect from '../TravelAsyncAutocompleteSelect/TravelAsyncAutocompleteSelect';
import * as icons from '../../../assets';
import './HotelOption.scss';
import { DatePicker } from '../../Common';

const HotelOption = forwardRef(
  (
    {
      index,
      control,
      trigger,
      getValues,
      setValue,
      headerDetails,
      hotelOptions,
      status,
      hotelBookingErrors,
      currentCompany,
    },
    ref,
  ) => {
    const { BaseCurr: baseCurr } = currentCompany || {};
    // Access the form control and validation methods
    const {
      activeOption,
      markOptionComplete,
      hotelInfo,
      setActiveOption,
      optionStatus,
      goNext,
      goPrev,
      setOptionComplete,
    } = useHotelBooking();

    const formatSelectValue = (val, max = 20) => {
      if (!val) return null;

      // If val is already { value, label }
      if (typeof val === 'object' && val !== null) {
        const value = val.value ?? '';
        const rawLabel = val.label ?? ''; // don't fall back to the object
        const sLabel = String(rawLabel);
        const label =
          sLabel.length > max ? `${sLabel.slice(0, max)}...` : sLabel;
        return { value, label };
      }

      // If val is a primitive (string/number)
      const s = String(val);
      const label = s.length > max ? `${s.slice(0, max)}...` : s;
      return { value: val, label };
    };

    const { data: travelAgencyOptions } = useQuery({
      queryKey: ['travelTypeOptions'],
      queryFn: () =>
        fetchTravelRequestEnquiryFilter({
          codeType: 'TRAVEL_AGENCY',
        }),
    });

    // Use the useFieldArray hook to manage the quotes array for this option
    const { fields, append, remove } = useFieldArray({
      control,
      name: `options.${index}.quotes`,
    });

    const hasAtLeastOneCompleteQuote = React.useCallback(
      values =>
        Array.isArray(values?.quotes) &&
        values.quotes.some(
          q => q?.country && q?.place && q?.checkInDate && q?.checkOutDate,
        ),
      [],
    );

    const hasRequiredHeaderFields = React.useCallback(
      values =>
        !!values?.agency &&
        !!values?.currency &&
        !!values?.quoteValue &&
        !!values?.ratesPerNight &&
        Number(values?.quoteValue) > 0,
      [],
    );

    const isOptionCompleteNow = React.useCallback(() => {
      const values = getValues(`options.${index}`);
      return (
        hasAtLeastOneCompleteQuote(values) && hasRequiredHeaderFields(values)
      );
    }, [getValues, index, hasAtLeastOneCompleteQuote, hasRequiredHeaderFields]);

    useEffect(() => {
      if (hotelOptions && hotelOptions?.segments?.length) {
        const mappedQuotes = hotelOptions.segments.map(seg => {
          const hotelCheckInDate = new Date(seg?.CheckInDate)
            .toISOString()
            .split('T')[0]; // 'YYYY-MM-DD'
          const hotelCheckOutDate = new Date(seg?.CheckOutDate)
            .toISOString()
            .split('T')[0]; // 'YYYY-MM-DD'

          seg.Country = seg.Country ? seg.Country.trim() : null;
          seg.Country_Description = seg.Country_Description
            ? seg.Country_Description.trim()
            : null;

          return {
            country:
              seg.Country && seg.Country_Description
                ? {
                    value: seg.Country,
                    label: seg.Country_Description || seg.Country,
                  }
                : null,
            countryName: seg.Country_Description || seg.Country,
            place: seg?.Place,
            checkInDate:
              hotelCheckInDate === '1900-01-01' ? '' : hotelCheckInDate,
            checkOutDate:
              hotelCheckOutDate === '1900-01-01' ? '' : hotelCheckOutDate,
            hotelName: seg?.HotelName,
            traveller: seg?.ClientHotel,
          };
        });

        // Set the quotes for this option
        setValue(`options.${index}.quotes`, mappedQuotes);
        const optNo = index + 1;

        setValue(
          `options.${index}.currency`,
          headerDetails?.[`Option${optNo}_Hotel_Curr`]?.trim() || '',
        );
        setValue(
          `options.${index}.ratesPerNight`,
          headerDetails?.[`Option${optNo}_Hotel_RatesPerNight`]?.trim() || '',
        );
        setValue(
          `options.${index}.quoteValue`,
          headerDetails?.[`Option${optNo}_Hotel_QuoteValue`] === undefined ||
            headerDetails?.[`Option${optNo}_Hotel_QuoteValue`] === null ||
            headerDetails?.[`Option${optNo}_Hotel_QuoteValue`] === '' ||
            headerDetails?.[`Option${optNo}_Hotel_QuoteValue`] === '0'
            ? ''
            : parseFloat(
                headerDetails?.[`Option${optNo}_Hotel_QuoteValue`],
              ).toFixed(2),
        );
        setValue(
          `options.${index}.terms`,
          headerDetails?.[`Option${optNo}_Hotel_Terms`]?.trim() || '',
        );
        setValue(
          `options.${index}.agency`,
          headerDetails?.[`Option${optNo}_Hotel_Travel_Agency`]?.trim() || '',
        );

        Promise.resolve().then(() => {
          // Prefer form-based check (quotes + headers)
          if (isOptionCompleteNow(getValues, index)) {
            markOptionComplete(index); // or setOptionComplete(index, true)
          } else {
            setOptionComplete(index, false);
          }
        });
      } else {
        setValue(`options.${index}.currency`, baseCurr || '');
      }
    }, [hotelOptions, headerDetails, baseCurr, index]);

    // Function to Clear Row
    const clearRow = rowIndex => {
      const basePath = `options.${index}.quotes.${rowIndex}`;

      if (fields.length === 1) {
        // Just clear the values if it's the only row
        const emptyRow = {
          country: null,
          countryName: '',
          place: '',
          checkInDate: '',
          checkOutDate: '',
          hotelName: '',
          traveller: '',
        };

        Object.entries(emptyRow).forEach(([fieldKey, emptyValue]) => {
          setValue(`${basePath}.${fieldKey}`, emptyValue);
        });
      } else {
        // Remove the row if there are multiple rows
        remove(rowIndex);
      }

      trigger(); // Refresh validation/UI
    };

    // Function to Add New Row if Last Row is Valid
    const addRowIfValid = () => {
      const last = getValues(`options.${index}.quotes.${fields.length - 1}`);

      const optionalFields = ['hotelName', 'traveller'];

      const isFilled = Object.entries(last).every(([key, value]) => {
        if (optionalFields.includes(key)) return true; // skip optional fields
        return (
          value !== undefined && value !== null && String(value).trim() !== ''
        );
      });

      if (isFilled) {
        append({
          country: null,
          countryName: '',
          place: '',
          checkInDate: '',
          checkOutDate: '',
          hotelName: '',
          traveller: '',
        });
      } else {
        toast.error(
          'Please fill all fields in the current row before adding a new one.',
          {
            className: 'toast-error',
          },
        );
      }
    };

    const renderHotelOptionRows = () =>
      fields.map((item, i) => (
        <tr key={item.id}>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.country`}
              control={control}
              render={({ field, fieldState }) => (
                <BookingAsyncAutocomplete
                  {...field}
                  error={!!fieldState.error}
                  autoCompleteFn={input =>
                    fetchPickListDetailsAutoComplete({
                      prefixText: input,
                      type: 'TRAVEL_COUNTRY',
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(country => ({
                      value: country.Code,
                      label: country.Description,
                    }))
                  }
                  value={formatSelectValue(field.value, 20)}
                  onChange={option => {
                    setValue(`options.${index}.quotes.${i}.country`, option, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setValue(
                      `options.${index}.quotes.${i}.countryName`,
                      option?.label || '',
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      },
                    );
                  }}
                  placeholder="Country"
                  isClearable
                  displayOption="bothCustom"
                  inputWidth="100%"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 2000 }),
                  }}
                />
              )}
            />
            {hotelBookingErrors?.options?.[index]?.quotes?.[0]?.countryName && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  hotelBookingErrors?.options?.[index]?.quotes?.[0]?.countryName
                    .message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.place`}
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  error={!!fieldState.error}
                  type="text"
                  {...field}
                  disabled={index > activeOption}
                  placeholder="Place"
                  onChange={val => field.onChange(val)}
                  className={`border-0 ${fieldState.error ? 'is-invalid' : ''}`}
                />
              )}
            />
            {hotelBookingErrors?.options?.[index]?.quotes?.[0]?.place && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  hotelBookingErrors?.options?.[index]?.quotes?.[0]?.place
                    .message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.checkInDate`}
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  disabled={index > activeOption}
                  borderStyle="none"
                  className={`border-0 ${fieldState.error ? 'is-invalid' : ''}`}
                  calendarFocus={false}
                />
              )}
            />
            {hotelBookingErrors?.options?.[index]?.quotes?.[0]?.checkInDate && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  hotelBookingErrors?.options?.[index]?.quotes?.[0]?.checkInDate
                    .message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.checkOutDate`}
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  disabled={index > activeOption}
                  borderStyle="none"
                  className={`border-0 ${fieldState.error ? 'is-invalid' : ''}`}
                  calendarFocus={false}
                />
              )}
            />
            {hotelBookingErrors?.options?.[index]?.quotes?.[0]
              ?.checkOutDate && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  hotelBookingErrors?.options?.[index]?.quotes?.[0]
                    ?.checkOutDate.message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.hotelName`}
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="Hotel Name"
                  {...field}
                  disabled={index > activeOption}
                  onChange={val => field.onChange(val)}
                  className="border-0"
                />
              )}
            />
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.traveller`}
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  {...field}
                  disabled={index > activeOption}
                  placeholder="Traveller"
                  onChange={val => field.onChange(val)}
                  className="border-0"
                />
              )}
            />
          </td>
          <td className="text-center p-0">
            <button
              type="button"
              className="btn btn-link py-1"
              onClick={() => clearRow(i)}
              title="Delete Row"
            >
              <Icon path={mdiTrashCanOutline} size={0.7} color="#e71d36" />
            </button>
          </td>
        </tr>
      ));

    const applyHotelInfo = () => {
      if (!hotelInfo || !Array.isArray(hotelInfo)) {
        toast.error('No hotel information to copy.', {
          className: 'toast-error',
        });
        return;
      }

      const quotes = hotelInfo.map(item => {
        const hotelCheckInDate = new Date(item?.CheckInDate)
          .toISOString()
          .split('T')[0]; // 'YYYY-MM-DD'
        const hotelCheckOutDate = new Date(item?.CheckOutDate)
          .toISOString()
          .split('T')[0]; // 'YYYY-MM-DD'

        return {
          country: {
            value: item.Country,
            label: item.Country_Description || item.Country,
          },
          countryName: item.Country_Description || item.Country,
          place: item?.Place,
          checkInDate: hotelCheckInDate,
          checkOutDate: hotelCheckOutDate,
          hotelName: item?.HotelName,
          traveller: item?.ClientHotel,
        };
      });

      // Replace all rows
      remove();
      quotes.forEach(q => append(q));
    };

    // Access the form methods from react-hook-form
    useImperativeHandle(ref, () => ({
      validate: async () => {
        const valid = await trigger(); // basic field validation
        const values = getValues(`options.${index}`); // get this option's full object

        const hasAtLeastOneQuote = values.quotes?.some(
          quote =>
            quote.country &&
            quote.place &&
            quote.checkInDate &&
            quote.checkOutDate &&
            quote.hotelName &&
            quote.traveller,
        );

        const hasRequiredFields =
          values.agency && values.currency && values.quoteValue;

        if (valid && hasAtLeastOneQuote && hasRequiredFields) {
          markOptionComplete(index);
          return true;
        }

        toast.error(
          'Please fill all required fields and at least one row of flight booking quotation.',
          {
            className: 'toast-error',
          },
        );
        return false;
      },
    }));

    // Check if the current index matches the active option
    const isRebookingMode =
      status === 'Request for Rebooking' ||
      status === 'Pending for Booking' ||
      status === 'Request for Cancellation';

    useEffect(() => {
      if (isRebookingMode && index !== activeOption) {
        setActiveOption(index);
      }
    });

    if (!isRebookingMode && index !== activeOption) return null;

    const getOptionClass = option => {
      if (activeOption === option) {
        return 'active';
      }
      if (optionStatus[option]) {
        return 'completed';
      }

      return 'invalid';
    };

    const getOptionCompleted = option => {
      if (optionStatus[option]) {
        return icons.iconsBaChecked;
      }
      return icons.iconsBaUnchecked;
    };

    const handleNext = async () => {
      const valid = await trigger(`options.${activeOption}`);
      const values = getValues(`options.${activeOption}`);

      const hasAtLeastOneQuote = values.quotes?.some(
        quote =>
          quote.country &&
          quote.place &&
          quote.checkInDate &&
          quote.checkOutDate,
      );

      const hasRequiredFields =
        values.agency &&
        values.currency &&
        values.quoteValue &&
        values.ratesPerNight;

      if (valid && hasAtLeastOneQuote && hasRequiredFields) {
        markOptionComplete(index);
        goNext();
        return true;
      }

      toast.error(
        'Please fill all required fields and at least one row of hotel booking quotation.',
        {
          className: 'toast-error',
        },
      );
      return false;
    };

    const handlePrev = () => {
      goPrev();
    };

    // Turn options into a clean array regardless of its shape
    function normalizeOptions(options) {
      if (Array.isArray(options)) return options;
      if (options && typeof options === 'object') {
        return Object.keys(options)
          .filter(k => String(+k) === k)
          .sort((a, b) => +a - +b)
          .map(k => options[k]);
      }
      return [];
    }

    // Deep-emptiness check: true means "no real error content inside"
    function isEmptyDeep(val) {
      if (val == null) return true;
      if (Array.isArray(val)) return val.every(isEmptyDeep);
      if (typeof val === 'object') {
        const vals = Object.values(val);
        if (vals.length === 0) return true; // {}
        return vals.every(isEmptyDeep);
      }
      return false;
    }

    function buildOptionErrorMessage(errors = {}) {
      const optionNumbers = new Set();

      const opts = normalizeOptions(errors.options);
      opts.forEach((opt, idx) => {
        if (!isEmptyDeep(opt)) {
          optionNumbers.add(idx + 1); // 1-based label
        }
      });

      if (optionNumbers.size === 0) return '';

      const labels = [...optionNumbers]
        .sort((a, b) => a - b)
        .map(n => `Option ${n}`);

      if (labels.length === 1) {
        return `Validation errors found in ${labels[0]}.`;
      }
      if (labels.length === 2) {
        return `Validation errors found in ${labels[0]} and ${labels[1]}.`;
      }
      const last = labels.pop();
      return `Validation errors found in ${labels.join(', ')} and ${last}.`;
    }

    return (
      <div
        className={`${hotelBookingErrors?.options?.root?.message ? 'is-invalid' : ''} ${hotelBookingErrors?.options?.length > 0 ? 'is-invalid' : ''}`}
      >
        <div className="quote-header pb-3">
          <div className="fw-semibold h5">Hotel Booking Options</div>
          {/* Options */}
          {!isRebookingMode && (
            <div className="quote-option-nav">
              <button
                type="button"
                onClick={handlePrev}
                disabled={activeOption === 0}
              >
                <Icon path={mdiChevronLeft} size={0.7} />
                <span>Previous</span>
              </button>
              <div className="quote-option-check">
                <div
                  className={`quote-option-check-item-0 ${getOptionClass(0)} ${activeOption === 0 ? 'option-check-active' : ''}`}
                >
                  <img
                    src={getOptionCompleted(0)}
                    alt="Checked Icon"
                    className="option-icon"
                  />
                  <span>Option 1</span>
                </div>
                <div
                  className={`quote-option-check-item-1 ${getOptionClass(1)} ${activeOption === 1 ? 'option-check-active' : ''}`}
                >
                  <img
                    src={getOptionCompleted(1)}
                    alt="Checked Icon"
                    className="option-icon"
                  />
                  <span>Option 2</span>
                </div>
                <div
                  className={`quote-option-check-item-2 ${getOptionClass(2)} ${activeOption === 2 ? 'option-check-active' : ''}`}
                >
                  <img
                    src={getOptionCompleted(2)}
                    alt="Checked Icon"
                    className="option-icon"
                  />
                  <span>Option 3</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleNext}
                disabled={activeOption === 2}
              >
                <span>Next</span>
                <Icon path={mdiChevronRight} size={0.7} />
              </button>
            </div>
          )}

          {/* Action Buttons */}
          {!isRebookingMode && (
            <div className="d-flex gap-2 justify-content-end mb-2">
              <button
                type="button"
                className="ba-action-buttons"
                onClick={addRowIfValid}
                disabled={index > activeOption}
              >
                <Icon path={mdiPlusCircleOutline} size={0.8} color="#026AA2" />
                <span>Add Row</span>
              </button>
              <button
                type="button"
                className="ba-action-buttons"
                onClick={applyHotelInfo}
              >
                <Icon path={mdiContentCopy} size={0.8} color="#026AA2" />
                <span>Copy</span>
              </button>
            </div>
          )}
        </div>
        <Form className="d-flex flex-column gap-2">
          <div className="table-responsive">
            <Table className="ba-option-table" id="ba-hotel-option-table">
              <thead>
                <tr>
                  {hotelBookingHeaders.map((header, i) => (
                    <th key={i}>
                      <span>{header.columnName}</span>
                      {header.required && (
                        <span className="text-danger-tr">*</span>
                      )}
                    </th>
                  ))}
                  <th className="text-center">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>{renderHotelOptionRows()}</tbody>
            </Table>
          </div>
          <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-row align-items-center justify-content-between gap-2">
              <Col md={3} className="external-filter-col">
                <span className="ba-form-label">
                  Currency<span className="text-danger-tr">*</span>
                </span>
                <Controller
                  name={`options.${index}.currency`}
                  control={control}
                  render={({ field }) => (
                    <TravelAsyncAutocompleteSelect
                      {...field}
                      placeholder="Select Currency"
                      autoCompleteFn={input =>
                        fetchPickListDetailsAutoComplete({
                          prefixText: input,
                          type: 'HR_CURRENCY',
                        })
                      }
                      mappedFn={rawData =>
                        rawData.map(currency => ({
                          value: currency.Code,
                          label: currency.Name,
                        }))
                      }
                      onChange={option =>
                        setValue(`options.${index}.currency`, option.value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                      value={field.value}
                    />
                  )}
                />
                {hotelBookingErrors?.options?.[index]?.currency && (
                  <span className="text-danger-tr">
                    {hotelBookingErrors.options[index].currency.message}
                  </span>
                )}
              </Col>
              <Col className="external-filter-col">
                <span className="ba-form-label">
                  Rates per Night<span className="text-danger-tr">*</span>
                </span>
                <Controller
                  control={control}
                  name={`options.${index}.ratesPerNight`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="0"
                      type="number"
                      className={`external-filter-input ${hotelBookingErrors?.options?.[index]?.ratesPerNight ? 'is-invalid' : ''}`}
                      onChange={val => field.onChange(val)}
                      onBlur={e => {
                        const val = e.target.value;
                        field.onChange(
                          val === '' ? '' : parseFloat(val).toFixed(2),
                        );
                      }}
                    />
                  )}
                />
                {hotelBookingErrors?.options?.[index]?.ratesPerNight && (
                  <span className="text-danger-tr">
                    {hotelBookingErrors.options[index].ratesPerNight.message}
                  </span>
                )}
              </Col>
              <Col className="external-filter-col">
                <span className="ba-form-label">
                  Quote Value<span className="text-danger-tr">*</span>
                </span>
                <Controller
                  control={control}
                  name={`options.${index}.quoteValue`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      placeholder="0"
                      className={`external-filter-input ${hotelBookingErrors?.options?.[index]?.quoteValue ? 'is-invalid' : ''}`}
                      onChange={val => field.onChange(val)}
                      onBlur={e => {
                        const val = e.target.value;
                        field.onChange(
                          val === '' ? '' : parseFloat(val).toFixed(2),
                        );
                      }}
                    />
                  )}
                />
                {hotelBookingErrors?.options?.[index]?.quoteValue && (
                  <span className="text-danger-tr">
                    {hotelBookingErrors.options[index].quoteValue.message}
                  </span>
                )}
              </Col>
            </div>
            <div className="d-flex flex-row align-items-center justify-content-between gap-2">
              <Col md={3} className="external-filter-col">
                <span className="ba-form-label">
                  Travel Agency<span className="text-danger-tr">*</span>
                </span>
                <Controller
                  control={control}
                  name={`options.${index}.agency`}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      onChange={val => field.onChange(val)}
                    >
                      <option value="">Select Agency</option>
                      {travelAgencyOptions?.map(option => (
                        <option key={option.Code} value={option.Code}>
                          {option.Description}
                        </option>
                      ))}
                    </Input>
                  )}
                />
                {hotelBookingErrors?.options?.[index]?.agency && (
                  <span className="text-danger-tr">
                    {hotelBookingErrors.options[index].agency.message}
                  </span>
                )}
              </Col>
              <Col className="external-filter-col">
                <span className="ba-form-label">Terms</span>
                <Controller
                  control={control}
                  name={`options.${index}.terms`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Terms"
                      type="text"
                      className="external-filter-input"
                      onChange={val => field.onChange(val)}
                    />
                  )}
                />
              </Col>
            </div>
          </div>
          <div className="text-danger-tr pt-3">
            {buildOptionErrorMessage(hotelBookingErrors)}
          </div>
          {hotelBookingErrors?.options?.root?.message && (
            <div className="text-danger-tr pt-3">
              {hotelBookingErrors?.options?.root?.message}
            </div>
          )}
        </Form>
      </div>
    );
  },
);

export default HotelOption;
